'use client'

import { useState, useEffect } from 'react'
import { AnimatePresence, motion } from 'motion/react'

import AppIcon from '@/components/content/app/AppIcon'

import type { ComparisonType } from '@/app/(app)/fetchers'
import type { App, ComparisonAppsApp } from '@/payload-types'
import { cn } from '@/lib/utils'

type ComparisonsTypeProps = {
  comparisons: ComparisonType[]
}

export default function ComparisonsType({ comparisons }: ComparisonsTypeProps) {
  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    if (!comparisons.length) return

    function changeIndex() {
      setCurrentIndex((prev) => {
        const next = prev + 1
        if (next >= comparisons.length) {
          return 0
        }
        return next
      })
    }

    changeIndex()

    // Set up interval for subsequent changes
    const interval = setInterval(() => {
      changeIndex()
    }, 5000)

    return () => clearInterval(interval)
  }, [comparisons.length])

  if (!comparisons.length) return null

  return (
    <div className="relative mt-auto h-[280px] w-full scale-75 py-5 md:scale-100">
      <div className="absolute left-1/2 top-1/2 z-20 flex h-7 -translate-x-1/2 -translate-y-7 items-center rounded-full border border-black/10 px-2 text-13 font-semibold leading-none text-black">
        VS
      </div>
      <div
        className="absolute left-1/2 top-1/2 z-10 flex h-[220px] -translate-x-1/2 -translate-y-1/2 items-center gap-24"
        style={{ perspective: 2000 }}
      >
        <AnimatePresence>
          {comparisons[currentIndex].comparisonApps?.docs
            ?.filter((doc): doc is ComparisonAppsApp => doc !== undefined)
            .map((comparisonApp, index) => {
              const app = comparisonApp.app as App

              return (
                <div
                  key={`${app.name}-${index}`}
                  className={cn(
                    'absolute left-1/2 top-0 flex flex-col items-center gap-4',
                    index === 0 ? '-translate-x-[230px] rotate-3' : 'translate-x-[50px] -rotate-3',
                  )}
                >
                  <motion.div
                    key={`${app.name}-${index}`}
                    initial={{
                      rotateX: index === 0 ? 90 : -90,
                      rotateY: index === 0 ? -5 : 5,
                      opacity: 0,
                      scale: 0.85,
                    }}
                    animate={{
                      rotateX: 0,
                      rotateY: 0,
                      opacity: 1,
                      scale: 1.02,
                      transition: {
                        rotateX: { type: 'spring', stiffness: 200, damping: 20, mass: 0.5 },
                        rotateY: { type: 'spring', stiffness: 200, damping: 20, mass: 0.5 },
                        opacity: { duration: 0.15 },
                        scale: { duration: 0.3, ease: [0.34, 1.56, 0.64, 1] },
                      },
                    }}
                    exit={{
                      rotateX: index === 0 ? -90 : 90,
                      rotateY: index === 0 ? 5 : -5,
                      opacity: 0,
                      scale: 0.85,
                      transition: {
                        rotateX: { type: 'spring', stiffness: 250, damping: 20, mass: 0.5 },
                        rotateY: { type: 'spring', stiffness: 250, damping: 20, mass: 0.5 },
                        opacity: { duration: 0.15 },
                        scale: { duration: 0.25 },
                      },
                    }}
                    style={{ transformStyle: 'preserve-3d', willChange: 'transform, opacity' }}
                  >
                    <div
                      style={{
                        perspective: 1200,
                        transformStyle: 'preserve-3d',
                        backfaceVisibility: 'hidden',
                      }}
                    >
                      <AppIcon
                        icon={app.icon}
                        glyph={app.glyph}
                        name={app.name}
                        slug={app.slug}
                        background={app.brandBackgroundColor}
                        size={182}
                        className="shadow-lg transition-shadow duration-300"
                      />
                    </div>
                  </motion.div>
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -20, opacity: 0 }}
                    transition={{
                      duration: 0.3,
                      ease: [0.165, 0.84, 0.44, 1.0],
                    }}
                    className="flex h-5 w-fit items-center overflow-hidden text-14 font-medium leading-none text-gray-950"
                  >
                    {app.name}
                  </motion.div>
                </div>
              )
            })}
        </AnimatePresence>
      </div>

      <div className="absolute left-0 top-6 hidden aspect-square w-[182px] -translate-x-[85%] -rotate-3 rounded-[48px] border border-black/10 bg-white shadow-lg md:block"></div>
      <div className="absolute right-0 top-6 hidden aspect-square w-[182px] translate-x-[85%] rotate-3 rounded-[48px] border border-black/10 bg-white shadow-lg md:block"></div>
    </div>
  )
}
